/**
 * useSearch — Production search hook for MRT PWA
 *
 * Ported from Sandvik Catalog useSearch.jsx with adaptations:
 *   - Uses dataService.getAllProducts() instead of static imports
 *   - Supports both TH and DTH product structures
 *   - Relevance scoring with competitor cross-reference matching
 *   - Type-ahead suggestions with category grouping
 *   - Highlight helper for match rendering
 *
 * Source: sandvik-catalog/src/hooks/useSearch.jsx (531 lines)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDebounce } from './useDebounce';

// ============================================
// CONFIGURATION
// ============================================

const DEFAULT_OPTIONS = {
  debounceMs: 300,
  minChars: 2,
  maxResults: 50,
  maxSuggestions: 8,
  searchFields: [
    'itemCode',
    'sku',
    'name',
    'description',
    // Rotary short + full descriptions (fall through to name/description for
    // TH/DTH where these fields are null).
    'descriptionShort',
    'descriptionFull',
    'descriptionLegacy',
    'thread',
    'rockDrillManufacturer',
    'compatibleRockDrill',
    'searchKeywords',
    // DTH-specific
    'hammerCompatibility.shankType',
    'hammerCompatibility.hammerFamily',
    'shankType',
    'hammerFamily',
    'bitFaceType',
    // Rotary-specific taxonomy / rig-fit fields.
    'rigPlatform',
    'rigBrand',
    'rigGroup',
    'threadUpper',
    'threadLower',
    'threadSingle',
    'application',
    // Rotary bits (Group R1) — family / IADC / pin / series / formation.
    'family',
    'iadcCode',
    'pinConnection',
    'seriesMarketing',
    'formationHardness',
    // Raiseboring (Group R2) — cutter model, reamer head type, stem fit.
    'model',
    'headType',
    'stemFit',
    'compatiblePairModel',
    'compatibleCutterModels',
    'internalPartNumber',
    // Services (Group S1) — grinding machines + cups.
    'buttonShape',
    'cupType',
    'capabilities',
    'internalDescription',
    // Competitor cross-reference
    'altPartEpiroc',
    'altPartRobit',
    'altPartRockmore',
    'altPartBoartLongyear',
    'altPartSaiDeepa',
    'altPartLhs',
    'altPartMitsubishi',
  ],
};

const COMPETITOR_FIELD_NAMES = {
  altPartEpiroc: 'Epiroc',
  altPartRobit: 'Robit',
  altPartRockmore: 'Rockmore',
  altPartBoartLongyear: 'Boart Longyear',
  altPartSaiDeepa: 'Sai-deepa',
  altPartLhs: 'LHS',
  altPartMitsubishi: 'Mitsubishi',
};

// ============================================
// SEARCH ALGORITHM
// ============================================

function getNestedValue(obj, path) {
  if (!path) return undefined;
  const parts = path.split('.');
  let value = obj;
  for (const part of parts) {
    if (value == null) return undefined;
    value = value[part];
  }
  return value;
}

function calculateScore(product, query, matchField) {
  const q = query.toLowerCase().trim();
  let score = 0;

  // Exact item code match — highest priority
  if (product.itemCode?.toLowerCase() === q) score += 1000;
  else if (product.itemCode?.toLowerCase().startsWith(q)) score += 500;
  else if (product.itemCode?.toLowerCase().includes(q)) score += 200;

  // Name / Description match
  const name = product.name || product.description;
  if (name?.toLowerCase() === q) score += 300;
  else if (name?.toLowerCase().startsWith(q)) score += 150;
  else if (name?.toLowerCase().includes(q)) score += 50;

  // Competitor part number — user looking for Sandvik equivalent
  if (matchField?.startsWith('altPart')) {
    const val = String(product[matchField] || '').toLowerCase();
    if (val === q) score += 800;
    else if (val.startsWith(q)) score += 400;
    else score += 250;
  }

  // DTH spec matches
  if (matchField?.includes('shankType') || matchField?.includes('hammerFamily')) {
    score += 75;
  }

  // Search keywords bonus
  if (matchField === 'searchKeywords') score += 150;

  // Field priority bonus
  if (matchField === 'itemCode') score += 100;
  if (matchField === 'name') score += 75;
  if (matchField === 'description') score += 50;
  if (matchField?.startsWith('altPart')) score += 75;

  return score;
}

function searchProducts(query, products, options = {}) {
  const { searchFields, maxResults } = { ...DEFAULT_OPTIONS, ...options };
  let normalizedQuery = query.toLowerCase().trim();

  // Strip manufacturer labels like "(Epiroc)" from query
  const mfgMatch = normalizedQuery.match(/^(.+?)\s*\(([^)]+)\)$/);
  let targetMfg = null;
  if (mfgMatch) {
    normalizedQuery = mfgMatch[1].trim();
    targetMfg = mfgMatch[2].toLowerCase();
  }

  if (!normalizedQuery || normalizedQuery.length < (options.minChars || 2)) return [];

  // Tokenised match — split the query on whitespace and require every token
  // to appear in at least one searched field. This lets multi-word queries
  // like "boom rig 76mm" hit products where the tokens are separated by
  // other words in the description.
  const tokens = normalizedQuery.split(/\s+/).filter((t) => t.length > 0);

  const results = [];
  const seen = new Set();

  const fieldContains = (product, field, token) => {
    const value = field.includes('.') ? getNestedValue(product, field) : product[field];
    if (!value) return false;
    if (Array.isArray(value)) {
      return value.some((item) => String(item).toLowerCase().includes(token));
    }
    return String(value).toLowerCase().includes(token);
  };

  for (const product of products) {
    if (seen.has(product.itemCode)) continue;

    // Every token must match at least one field. Track the "best" field
    // (first match on the highest-priority field per token) for scoring.
    let bestField = null;
    let allTokensMatched = true;

    for (const token of tokens) {
      let tokenField = null;
      for (const field of searchFields) {
        if (fieldContains(product, field, token)) {
          tokenField = field;
          break;
        }
      }
      if (!tokenField) { allTokensMatched = false; break; }
      // First-token field wins bestField for scoring anchor.
      if (!bestField) bestField = tokenField;
    }

    if (!allTokensMatched) continue;

    // Apply manufacturer-label filter if present ("(Epiroc)" etc.).
    let matchField = bestField;
    if (targetMfg) {
      const expectedField = Object.entries(COMPETITOR_FIELD_NAMES)
        .find(([, m]) => m.toLowerCase() === targetMfg)?.[0];
      if (expectedField && tokens.every((t) => fieldContains(product, expectedField, t))) {
        matchField = expectedField;
      }
    }

    const score = calculateScore(product, normalizedQuery, matchField);
    let competitorMatch = null;
    if (matchField?.startsWith('altPart')) {
      competitorMatch = {
        manufacturer: COMPETITOR_FIELD_NAMES[matchField] || matchField,
        partNumber: product[matchField],
      };
    }
    results.push({
      ...product,
      _searchScore: score,
      _matchField: matchField,
      _competitorMatch: competitorMatch,
    });
    seen.add(product.itemCode);
  }

  return results.sort((a, b) => b._searchScore - a._searchScore).slice(0, maxResults);
}

function generateSuggestions(query, products, maxSuggestions = 8) {
  const q = query.toLowerCase().trim();
  if (!q || q.length < 2) return [];

  const suggestions = [];
  const seenTexts = new Set();

  for (const product of products) {
    // Item code suggestions
    if (product.itemCode?.toLowerCase().includes(q)) {
      const text = product.itemCode;
      if (!seenTexts.has(text.toLowerCase())) {
        suggestions.push({ type: 'itemCode', text, product, productLine: product._domain || 'top-hammer' });
        seenTexts.add(text.toLowerCase());
      }
    }

    // Name / description suggestions
    const name = product.name || product.description;
    if (name?.toLowerCase().includes(q)) {
      const text = name.split(' ').slice(0, 5).join(' ');
      if (!seenTexts.has(text.toLowerCase()) && text.length > q.length) {
        suggestions.push({ type: 'description', text, product, productLine: product._domain || 'top-hammer' });
        seenTexts.add(text.toLowerCase());
      }
    }

    // Competitor part suggestions
    for (const [field, mfg] of Object.entries(COMPETITOR_FIELD_NAMES)) {
      const pn = product[field];
      if (pn && pn !== 'None') {
        const pnStr = String(pn);
        if (pnStr.toLowerCase().includes(q)) {
          const text = `${pnStr} (${mfg})`;
          if (!seenTexts.has(pnStr.toLowerCase())) {
            suggestions.push({ type: 'competitor', text, partNumber: pnStr, manufacturer: mfg, product, productLine: product._domain || 'top-hammer' });
            seenTexts.add(pnStr.toLowerCase());
          }
        }
      }
    }

    // Search keywords
    if (Array.isArray(product.searchKeywords)) {
      for (const kw of product.searchKeywords) {
        const kwLower = String(kw).toLowerCase();
        if (kwLower.includes(q) && !seenTexts.has(kwLower)) {
          suggestions.push({ type: 'keyword', text: kw, product, productLine: product._domain || 'top-hammer' });
          seenTexts.add(kwLower);
        }
      }
    }

    if (suggestions.length >= maxSuggestions * 3) break;
  }

  return suggestions
    .sort((a, b) => {
      const aExact = a.text.toLowerCase().startsWith(q) ? 0 : 1;
      const bExact = b.text.toLowerCase().startsWith(q) ? 0 : 1;
      if (aExact !== bExact) return aExact - bExact;
      const order = { itemCode: 0, keyword: 1, competitor: 2, description: 3 };
      const aO = order[a.type] ?? 3;
      const bO = order[b.type] ?? 3;
      if (aO !== bO) return aO - bO;
      return a.text.length - b.text.length;
    })
    .slice(0, maxSuggestions);
}

function groupByCategory(results) {
  const groups = {};
  for (const product of results) {
    const categoryId = product.subcategory || product.category || 'uncategorized';
    if (!groups[categoryId]) {
      groups[categoryId] = {
        categoryId,
        categoryName: product.categoryLabel || product.subcategoryLabel || categoryId,
        productLine: product._domain || 'top-hammer',
        products: [],
      };
    }
    groups[categoryId].products.push(product);
  }
  return Object.values(groups).sort((a, b) => b.products.length - a.products.length);
}

// ============================================
// HOOK
// ============================================

/**
 * @param {string} query - Search query string
 * @param {object} options - { products, debounceMs, minChars, maxResults, maxSuggestions }
 */
export default function useSearch(query, options = {}) {
  const merged = { ...DEFAULT_OPTIONS, ...options };
  const debouncedQuery = useDebounce(query, merged.debounceMs);

  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);

  const products = options.products || [];

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < merged.minChars) {
      setResults([]);
      setSuggestions([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      const searchResults = searchProducts(debouncedQuery, products, merged);
      const searchSuggestions = generateSuggestions(debouncedQuery, products, merged.maxSuggestions);
      setResults(searchResults);
      setSuggestions(searchSuggestions);
    } catch (err) {
      console.error('[useSearch] Error:', err);
      setError(err.message);
      setResults([]);
      setSuggestions([]);
    } finally {
      setIsSearching(false);
    }
  }, [debouncedQuery, products]);

  const isPending = query !== debouncedQuery && query.length >= merged.minChars;

  const groupedResults = useMemo(() => groupByCategory(results), [results]);

  const highlightMatch = useCallback(
    (text, highlightQuery = query) => {
      if (!text || !highlightQuery || highlightQuery.length < 2) return text;
      const idx = text.toLowerCase().indexOf(highlightQuery.toLowerCase());
      if (idx === -1) return text;
      const before = text.slice(0, idx);
      const match = text.slice(idx, idx + highlightQuery.length);
      const after = text.slice(idx + highlightQuery.length);
      return (
        <>
          {before}
          <mark className="bg-yellow-200 text-carbon-black px-0.5 rounded">{match}</mark>
          {after}
        </>
      );
    },
    [query]
  );

  const clearSearch = useCallback(() => {
    setResults([]);
    setSuggestions([]);
  }, []);

  return {
    query: debouncedQuery,
    originalQuery: query,
    results,
    suggestions,
    groupedResults,
    resultCount: results.length,
    isSearching: isSearching || isPending,
    isPending,
    error,
    hasResults: results.length > 0,
    highlightMatch,
    clearSearch,
  };
}

// Standalone exports for use outside the hook
export { searchProducts, generateSuggestions, groupByCategory, calculateScore };
